import { useEffect, useState, useSyncExternalStore, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';

// Imperative APIs (toast(), confirmDialog()) render through a host component the
// shell mounts once. If none is mounted yet, the first call mounts one in its
// own root so the API never silently drops a request. Only the first
// registered host renders, so a later shell-mounted host cannot duplicate it.

export interface HostRegistry {
  /** Call inside the host component; true for the one host that should render. */
  usePrimaryHost(): boolean;
  /** Mounts a standalone host when none is registered. */
  ensureMounted(): void;
}

export function createHostRegistry(name: string, getHost: () => ComponentType): HostRegistry {
  const hosts: symbol[] = [];
  const listeners = new Set<() => void>();
  let standalone = false;
  const notify = () => listeners.forEach((l) => l());
  const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  };

  return {
    usePrimaryHost() {
      const [id] = useState(() => Symbol(name));
      useEffect(() => {
        hosts.push(id);
        notify();
        return () => {
          const index = hosts.indexOf(id);
          if (index >= 0) hosts.splice(index, 1);
          notify();
        };
      }, [id]);
      return useSyncExternalStore(subscribe, () => hosts[0] === id, () => false);
    },
    ensureMounted() {
      if (hosts.length > 0 || standalone || typeof document === 'undefined') return;
      standalone = true;
      const container = document.createElement('div');
      container.setAttribute('data-host', name);
      document.body.appendChild(container);
      const Host = getHost();
      createRoot(container).render(<Host />);
    },
  };
}
